(() => {
  'use strict';

  if (window.__pwSharedGiftEngineV1) return;
  window.__pwSharedGiftEngineV1 = true;

  const RULES_ID = 'pw-gift-rules-data';

  function parseJson(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '');
    } catch (error) {
      console.error('[PUNCH gift engine] Invalid JSON', error);
      return null;
    }
  }

  function asStringId(value) {
    return value == null ? '' : String(value);
  }

  function idSet(values) {
    return new Set((values || []).map(asStringId));
  }

  function intersects(values, set) {
    return (values || []).some((value) => set.has(asStringId(value)));
  }

  function money(minorUnits, currency) {
    const amount = (Number(minorUnits) || 0) / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (_) {
      return `${currency || ''} ${amount.toFixed(2)}`.trim();
    }
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function thresholdMinor(rule, currency) {
    const thresholds = rule.thresholds || {};
    const major = Number(
      Object.prototype.hasOwnProperty.call(thresholds, currency)
        ? thresholds[currency]
        : thresholds.fallback
    ) || 0;

    return Math.round(major * 100);
  }

  function renderGift(ruleState, currency) {
    const { rule, matchedSubtotal, threshold, qualified, allGiftsAdded } = ruleState;
    const gifts = rule.gifts || [];
    const copy = rule.copy || {};
    const remaining = Math.max(0, threshold - matchedSubtotal);
    const progress = threshold > 0
      ? Math.min(100, (matchedSubtotal / threshold) * 100)
      : 100;

    const giftImages = gifts.slice(0, 3).map((gift) => {
      if (!gift.image) return '';
      return `
        <a href="${escapeHtml(gift.url || '#')}" class="pw-cart-page__gift-product" tabindex="-1" aria-hidden="true">
          <img
            src="${escapeHtml(gift.image)}"
            alt=""
            loading="lazy"
            class="pw-cart-page__gift-image"
          >
        </a>
      `;
    }).join('');

    let heading = '';
    let message = '';
    let meta = '';

    if (allGiftsAdded) {
      heading = gifts.length > 1 ? 'Gifts added' : 'Gift added';
      message = copy.addedMessage || 'Your complimentary gift is included with this order.';
      meta = '<span class="pw-cart-page__gift-status pw-cart-page__gift-status--added">â ADDED</span>';
    } else if (qualified) {
      heading = copy.unlockedHeading || 'Free gift unlocked';
      message = copy.unlockedMessage || 'Your order now qualifies for this complimentary gift.';
      meta = '<span class="pw-cart-page__gift-status pw-cart-page__gift-status--unlocked">UNLOCKED</span>';
    } else {
      heading = `${money(remaining, currency)} more to unlock`;
      message = copy.lockedMessage || 'Complimentary with qualifying orders.';
      meta = copy.endText
        ? `<span class="pw-cart-page__gift-end">${escapeHtml(copy.endText)}</span>`
        : '<span class="pw-cart-page__gift-status">GIFT</span>';
    }

    const giftTitles = gifts.map((gift) => escapeHtml(gift.title || '')).filter(Boolean).join(' + ');

    return `
      <div class="pw-cart-page__gift pw-cart-page__gift--rule ${qualified ? 'is-unlocked' : 'is-locked'} ${allGiftsAdded ? 'is-added' : ''}">
        <div class="pw-cart-page__gift-products" aria-hidden="true">
          ${giftImages}
        </div>

        <div class="pw-cart-page__gift-copy">
          <span class="pw-cart-page__gift-label">
            ${escapeHtml(copy.kicker || rule.ruleName || 'Free gift')}
          </span>

          <strong>${escapeHtml(heading)}</strong>

          <span class="pw-cart-page__gift-message">${escapeHtml(message)}</span>

          ${giftTitles ? `<span class="pw-cart-page__gift-titles">${giftTitles}</span>` : ''}

          ${threshold > 0 ? `
            <div class="pw-cart-page__gift-progress" aria-label="Gift qualification progress">
              <span style="width:${progress}%"></span>
            </div>
            <span class="pw-cart-page__gift-spend">
              Qualifying spend ${escapeHtml(money(matchedSubtotal, currency))} / ${escapeHtml(money(threshold, currency))}
            </span>
          ` : ''}
        </div>

        <div class="pw-cart-page__gift-meta">
          ${meta}
        </div>
      </div>
    `;
  }

  function evaluate(rulesData, cartContext) {
    const globalProductExclusions = idSet(
      rulesData.globalExclusions && rulesData.globalExclusions.productIds
    );
    const globalCollectionExclusions = idSet(
      rulesData.globalExclusions && rulesData.globalExclusions.collectionIds
    );

    const activeRules = (rulesData.rules || []).filter((rule) => rule && rule.enabled);
    const allGiftProductIds = new Set();

    activeRules.forEach((rule) => {
      (rule.gifts || []).forEach((gift) => {
        if (gift && gift.productId != null) allGiftProductIds.add(asStringId(gift.productId));
      });
    });

    const claimedKeys = new Set();
    const cartProductIds = idSet((cartContext.items || []).map((item) => item.productId));
    const states = [];

    activeRules.forEach((rule) => {
      const includeProducts = idSet(rule.includeProductIds);
      const includeCollections = idSet(rule.includeCollectionIds);
      const excludeProducts = idSet(rule.excludeProductIds);
      const excludeCollections = idSet(rule.excludeCollectionIds);

      let matchedSubtotal = 0;
      const newlyClaimedKeys = [];

      (cartContext.items || []).forEach((item) => {
        const key = asStringId(item.key);
        const productId = asStringId(item.productId);
        const collectionIds = item.collectionIds || [];

        if (claimedKeys.has(key)) return;

        // A configured gift never contributes to qualifying spend.
        if (allGiftProductIds.has(productId)) return;

        const included =
          includeProducts.has(productId) ||
          intersects(collectionIds, includeCollections);

        if (!included) return;

        const excluded =
          globalProductExclusions.has(productId) ||
          intersects(collectionIds, globalCollectionExclusions) ||
          excludeProducts.has(productId) ||
          intersects(collectionIds, excludeCollections);

        if (excluded) return;

        matchedSubtotal += Number(item.finalLinePrice) || 0;
        newlyClaimedKeys.push(key);
      });

      // Preserve the original drawer behaviour:
      // a rule only claims lines if at least one line matched.
      newlyClaimedKeys.forEach((key) => claimedKeys.add(key));

      const gifts = rule.gifts || [];
      if (!newlyClaimedKeys.length || !gifts.length) return;

      const threshold = thresholdMinor(rule, cartContext.currency);
      const qualified = threshold <= 0 || matchedSubtotal >= threshold;
      const allGiftsAdded = gifts.every((gift) =>
        cartProductIds.has(asStringId(gift.productId))
      );

      states.push({
        rule,
        matchedSubtotal,
        threshold,
        qualified,
        allGiftsAdded
      });
    });

    return states;
  }

  function renderEngine(engine) {
    const output = engine.querySelector('[data-pw-gift-output]');
    const contextNode = engine.querySelector('[data-pw-gift-cart-context]');
    const rulesNode = document.getElementById(RULES_ID);

    if (!output || !contextNode || !rulesNode) return;

    const cartContext = parseJson(contextNode);
    const rulesData = parseJson(rulesNode);

    if (!cartContext || !rulesData) {
      output.replaceChildren();
      return;
    }

    const states = evaluate(rulesData, cartContext);
    output.innerHTML = states.map((state) => renderGift(state, cartContext.currency)).join('');
  }

  function renderAll() {
    document.querySelectorAll('[data-pw-gift-engine]').forEach(renderEngine);
  }

  function scheduleRender() {
    window.requestAnimationFrame(renderAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderAll, { once: true });
  } else {
    renderAll();
  }

  // Re-render after native/theme cart refreshes without polling.
  document.addEventListener('cart:change', scheduleRender);
  document.addEventListener('shopify:section:load', scheduleRender);
})();
